import { Feature, LineString, Point, Position } from "geojson";
import { MapMouseEvent } from "@mapbox/mapbox-gl-draw";
import { AbstractDraw, DrawInteractionState } from "./abstract-draw";
import { GeometryUtil } from "../geometry";
import { SnapPoint } from "../snap";
import { FeatureUtil } from "../feature";
import { GeometryCoordinate } from "../interaction-mode/abstract-interaction-mode";
import { VueMapPopup } from "../control/VueMapPopup";
import { defineAsyncComponent } from "vue";

let MapInputNumberMarker = defineAsyncComponent(() => import("../../../components/map/markers/MapInputNumber.vue"));

export abstract class AbstractDrawLinestring extends AbstractDraw<DrawInteractionState> {
  protected line: Feature<LineString> | undefined;
  protected lastPoint: Feature<Point> | undefined


  buildInitialState(initialState: DrawInteractionState): DrawInteractionState {
    let state = super.buildInitialState(initialState);

    if (state.vertexList.length > 0) {
      this._generateDrawLine(state);
    }
    if (state.vertexList.length > 1) {
      this._generateLastPoint(state);
    }

    return state;
  }

  canSave(state: DrawInteractionState): boolean {
    return state.vertexList.length > 1;
  }

  async onMouseMove(state: DrawInteractionState, e: MapMouseEvent) {
    this.snapPoint = await this._getSnapPoint(state, e);

    this.aim.geometry.coordinates = this.snapPoint.point as Position;

    this.displayDrawFeature(this.aim);

    if (!state.isDirty) {
      return;
    }

    this._generateLengthInput(state);

    if (this.line) {
      if (state.vertexPosition > 1) {
        this._generateLastPoint(state);
      }
      this._generateDrawLine(state, this.snapPoint);
    }
  }

  async onClick(state: any, e: MapMouseEvent) {
    await super.onClick(state, e);

    if (e.defaultPrevented) {
      return;
    }

    this._generateLastPoint(state);

    this._generateDrawLine(state);
    this.displayDrawFeature(this.line);

    this.tick();
  }

  async setCoordinates(state: DrawInteractionState, points: Position[]): Promise<void> {
    await super.setCoordinates(state, points);

    this._generateLastPoint(state);

    this._generateDrawLine(state);

    this.tick();
  }

  save(state: any, nextInteraction: string | undefined = undefined) {
    this.createLayerGeometries([GeometryUtil.lineString(state.vertexList)], nextInteraction)
  }

  protected _generateLastPoint(state: DrawInteractionState): void {
    if (state.vertexPosition < 2) {
      if (this.lastPoint) {
        this.removeDrawFeature(this.lastPoint.id.toString());
        this.lastPoint = undefined;
      }
      return;
    }
    this.lastPoint = this.createIcon(
      state.vertexList[state.vertexPosition - 1],
      'map-check',
      {
        parent: this.line.id.toString(),
        coord_path: (state.vertexPosition - 1).toString(),
        id: 'last_point',
        'icon-size': 0.5
      }
    );
    this.displayDrawFeature(this.lastPoint);
  }

  protected _generateDrawLine(state: DrawInteractionState, lastPoint: SnapPoint | undefined = undefined): void {
    if (state.vertexPosition === 0) {
      if (this.line) {
        this.removeDrawFeature(this.line.id.toString());
        this.line = undefined;
      }
      return;
    }
    let vertexList = [...state.vertexList];

    if (lastPoint) {
      if (this.controller.snapUtility.isMode('trace') && state.snapFeature) {
        let trace = FeatureUtil.extractLineFrom(state.snapFeature.geometry as any, state.snapFeaturePath, lastPoint.path);
        vertexList.push(...trace);
      }
      vertexList.push(lastPoint.point);
    }

    this.line = this.createLineString(vertexList, 'tmp_line');
    this.line.properties._showLength = this.controller.interaction.hasLength();
    this.line.properties._showVertexLength = this.controller.interaction.hasVertexLength();
    this.line.properties._showAngle = this.controller.interaction.hasAngle();
    this.displayDrawFeature(this.line);
  }
}
